import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Search, Menu,
  ChevronDown, Check, Star, X, Layers,
  ListChecks, Users, CalendarRange
} from 'lucide-react';
import UserMenu from '../UserMenu/UserMenu';
import NotificationBell from '../NotificationBell/NotificationBell';
import GlobalSearchBar from '../GlobalSearch/GlobalSearchBar';
import { TimeRangePicker } from '@techstack/ui';
import { useTimeRangeValue } from '../../hooks/useTimeRangeValue';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';
import { buildFuse, searchDepartments, sortDepartments } from './departmentSearch';
import './TopNav.css';
import { Hint } from '@techstack/ui';
import { AgencyLogo, useAgencyLogos } from '../Accounts/AgencyLogo';
import DepartmentFacts from './DepartmentFacts';

const TopNav = ({
  selectedDepartment,
  departments = [],
  onDepartmentSelect,
  onSelectAllDepartments,
  currentUser,
  onSettingsClick,
  onLogout,
  onThemeToggle,
  theme,
  notifications = [],
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  onClearNotifications,
  onToggleSidebar,
  sidebarMode,
  pinnedDepartments = [],
  departmentOrder = [],
  onTogglePin,
  onReorderPins,
  // Pages that show state-wide aggregates (e.g. /vendor-analysis) pass
  // hideDepartmentFilter=true so the dept selector + favorite chips are
  // suppressed. Showing them implies a filter that doesn't apply, which
  // is misleading on a global view.
  hideDepartmentFilter = false,
  statewideLabel = null,
  // Routes that own their ENTIRE filter surface (e.g. /tasks — the funnel's
  // TaskFilterBar) pass filterHidden=true: the global agency/account filter
  // chrome renders nothing at all. State is untouched — pure chrome removal
  // (Ryan 2026-08-07: "it should not coexist with the funnel").
  filterHidden = false,
  // Account list mode (e.g. /sales-ai) - shows account list as primary selector
  accountListMode = false,
  // Account lists for filtering by saved account groups
  accountLists = [],
  selectedAccountListId,
  selectedAccountList,
  accountListMembers = [],
  onAccountListSelect,
  onAccountListClear,
  // Hide hamburger menu on fullscreen routes (e.g., Sales AI)
  hideHamburger = false,
}) => {
  // Global time filter (invariant 7: it lives in the shell). The picker
  // reads/writes TimeFilterContext, so every view that consumes the
  // context — Sankey, Tech Stack, VCA — follows this one control. The
  // full pill strip is too wide for the topbar, so it lives inside a
  // compact dropdown anchored to a small button.
  const { timeRangeValue, setTimePeriod } = useTimeRangeValue();
  // The shell time filter's FY jumps follow the same basis as the charts.
  const { calendar: fiscalCalendar } = useFiscalBasisContext();
  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const timeDropdownRef = useRef(null);
  const TIME_LABELS = {
    '1m': '1m', '3m': '3m', '6m': '6m', '1y': '1y', '2y': '2y',
    '3y': '3y', '5y': '5y', all: 'All time', custom: 'Custom',
  };
  const timeLabel = TIME_LABELS[timeRangeValue?.preset] || '3y';

  // One logo, fetched once for the selected agency. The account page used to
  // fetch the same mark for its own header; that header is gone, so this is
  // now the only place it is asked for.
  const deptLogoMap = useAgencyLogos([selectedDepartment?.department_id]);
  const deptLogoUrl = deptLogoMap[selectedDepartment?.department_id];

  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Account list dropdown state
  const [accountListDropdownOpen, setAccountListDropdownOpen] = useState(false);
  const accountListDropdownRef = useRef(null);

  // The quick account picker: one button, one menu, in place of the chip row
  // and the separate list switcher (Ryan, 2026-09-13: "a button with a quick
  // account picker that you click and a drop down of all your accounts").
  const [accountsOpen, setAccountsOpen] = useState(false);
  const accountsRef = useRef(null);

  // Search collapses to a magnifier. The bar stays MOUNTED (its ⌘K listener
  // must keep working) and only its width is taken away; focus — from the
  // button or from ⌘K — opens it, and leaving it closes it.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDeptDropdownOpen(false);
        setSearchQuery('');
      }
      if (accountListDropdownRef.current && !accountListDropdownRef.current.contains(event.target)) {
        setAccountListDropdownOpen(false);
      }
      if (accountsRef.current && !accountsRef.current.contains(event.target)) {
        setAccountsOpen(false);
      }
      if (timeDropdownRef.current && !timeDropdownRef.current.contains(event.target)) {
        setTimeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (deptDropdownOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [deptDropdownOpen]);

  // Build Fuse index once per department list change
  const fuse = useMemo(() => buildFuse(departments), [departments]);

  // Sort: pinned first (in user-specified order), then has_data, then total_spend desc
  const sortedDepartments = useMemo(
    () => sortDepartments(departments, pinnedDepartments, departmentOrder),
    [departments, pinnedDepartments, departmentOrder]
  );

  // When searching, use Fuse with alias expansion; otherwise show the sorted list
  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) return sortedDepartments;
    const results = searchDepartments(fuse, searchQuery, departments);
    return results || [];
  }, [searchQuery, sortedDepartments, fuse, departments]);

  const handleDepartmentSelect = (dept) => {
    onDepartmentSelect?.(dept.department_id);
    setDeptDropdownOpen(false);
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    setDeptDropdownOpen(prev => !prev);
    if (deptDropdownOpen) {
      setSearchQuery('');
    }
  };

  const handleTogglePin = (e, deptId) => {
    e.stopPropagation();
    onTogglePin?.(deptId);
  };

  // Account list handlers
  const handleAccountListSelect = (listId) => {
    onAccountListSelect?.(listId);
    setAccountListDropdownOpen(false);
  };

  const handleAccountListClear = () => {
    onAccountListClear?.();
    setAccountListDropdownOpen(false);
  };

  const handleAccountMemberClick = (member) => {
    if (member.department_id) {
      onDepartmentSelect?.(member.department_id);
    }
    setAccountsOpen(false);
  };

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchWrapRef.current?.querySelector('input')?.focus(), 0);
  };

  // Build ordered list of pinned department objects
  const orderedPinnedDepts = (() => {
    const pinnedSet = new Set(pinnedDepartments.map(String));
    const deptMap = new Map(departments.map(d => [String(d.department_id), d]));

    // Use departmentOrder if available, otherwise use pinnedDepartments order
    const orderSource = departmentOrder.length > 0 ? departmentOrder : pinnedDepartments;
    const ordered = [];
    const seen = new Set();

    for (const id of orderSource) {
      const strId = String(id);
      if (pinnedSet.has(strId) && deptMap.has(strId) && !seen.has(strId)) {
        ordered.push(deptMap.get(strId));
        seen.add(strId);
      }
    }

    // Add any pinned departments not in the order list
    for (const id of pinnedDepartments) {
      const strId = String(id);
      if (!seen.has(strId) && deptMap.has(strId)) {
        ordered.push(deptMap.get(strId));
      }
    }

    return ordered;
  })();

  const isPinned = (deptId) => pinnedDepartments.map(String).includes(String(deptId));

  // What the quick picker lists: the selected list's members, else the
  // pinned favourites. Both are agencies the rep chose to keep close.
  const quickRows = selectedAccountList
    ? accountListMembers.map((m) => ({
        key: m.id, department_id: m.department_id,
        acronym: m.acronym, name: m.official_name || m.common_name || m.salesforce_account_name,
      }))
    : orderedPinnedDepts.map((d) => ({
        key: d.department_id, department_id: d.department_id,
        acronym: d.acronym, name: d.official_name || d.common_name,
      }));
  const quickLabel = selectedAccountList ? selectedAccountList.name : 'Favorites';
  // Shown as the menu's own heading; the button always says "My Accounts".

  return (
    <nav className="topnav">
      {/* ONE ROW. It was two — a slim brand/search bar over a 44px filter
          bar — and with the breadcrumb under them the eye crossed three bands
          before the page. Everything that was a strip is now a menu: the
          agency is the anchor, the accounts sit behind one button, search
          is a magnifier until it is wanted (Ryan, 2026-09-13). */}
      <div className="topnav-container">
        {/* The brand lives at the top of the rail now (the Anthropic /
            OpenAI shape: product mark in the sidebar, the bar is all
            context). The hamburger survives only where the rail cannot be
            reached: collapsed on desktop, and on phones where it is a drawer. */}
        {!hideHamburger && (
          <button
            className={`topnav-hamburger ${sidebarMode === 'collapsed' ? '' : 'topnav-hamburger--mobile-only'}`}
            onClick={onToggleSidebar}
            aria-label="Toggle navigation"
            title={sidebarMode === 'collapsed' ? 'Show navigation' : 'Navigation'}
          >
            <Menu size={20} />
          </button>
        )}

        {!filterHidden && (
          <div className="topnav-context">
            {hideDepartmentFilter && !accountListMode ? (
              <div
                className="statewide-badge"
                title="This page shows California state-wide aggregates. Agency filtering does not apply."
              >
                <Layers size={14} />
                <span>{statewideLabel || 'California (state-wide)'}</span>
              </div>
            ) : accountListMode ? (
            /* Account List Mode - Sales AI Intelligence */
            <div className="account-list-mode">
              <div className="account-list-selector-primary" ref={accountListDropdownRef}>
                <button
                  className={`account-list-primary-btn ${accountListDropdownOpen ? 'open' : ''} ${selectedAccountList ? 'has-selection' : ''}`}
                  onClick={() => setAccountListDropdownOpen(prev => !prev)}
                  aria-expanded={accountListDropdownOpen}
                  aria-haspopup="listbox"
                  title={selectedAccountList ? `Working with: ${selectedAccountList.name}` : 'Select an account list'}
                >
                  <Users size={16} />
                  <span className="account-list-primary-label">
                    {selectedAccountList ? selectedAccountList.name : 'Select Account List'}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`account-list-chevron ${accountListDropdownOpen ? 'rotated' : ''}`}
                  />
                </button>

                {accountListDropdownOpen && (
                  <div className="account-list-dropdown account-list-dropdown-primary">
                    <div className="account-list-dropdown-header">
                      <span>My Account Lists</span>
                      {selectedAccountList && (
                        <button
                          className="account-list-clear-btn"
                          onClick={handleAccountListClear}
                          title="Clear list filter"
                        >
                          <X size={12} /> Clear
                        </button>
                      )}
                    </div>
                    <div className="account-list-dropdown-items">
                      {accountLists.length > 0 ? (
                        accountLists.map(list => {
                          const isSelected = selectedAccountListId === list.id;
                          return (
                            <button
                              key={list.id}
                              className={`account-list-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleAccountListSelect(list.id)}
                            >
                              <Users size={14} className="account-list-item-icon" />
                              <div className="account-list-item-info">
                                <span className="account-list-item-name">{list.name}</span>
                                <span className="account-list-item-count">
                                  {list.member_count} account{list.member_count === 1 ? '' : 's'}
                                </span>
                              </div>
                              {isSelected && <Check size={14} className="account-list-item-check" />}
                            </button>
                          );
                        })
                      ) : (
                        <div className="account-list-empty">
                          <span>No account lists yet</span>
                          <span className="account-list-empty-hint">Create one in My Lists</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Account list mode shows just the list selector, no individual member chips */}
            </div>
            ) : (
              <>
          {/* Agency — the anchor of the row. Its menu also holds "All agencies". */}
          <div className="department-selector" ref={dropdownRef}>
            <button
              className={`department-context ${deptDropdownOpen ? 'open' : ''} ${!selectedDepartment ? 'all-selected' : ''}`}
              onClick={toggleDropdown}
              aria-expanded={deptDropdownOpen}
              aria-haspopup="listbox"
            >
              {selectedDepartment ? (
                <>
                  {/* The agency's own mark, not a text badge. It was already
                      being fetched for the page below; showing it HERE is what
                      lets the second header go away entirely. AgencyLogo falls
                      back to the acronym chip when a department has no mark,
                      so nothing regresses for the ~100 that do not. */}
                  <AgencyLogo
                    url={deptLogoUrl}
                    acronym={selectedDepartment.acronym}
                    badgeClass="department-badge"
                    logoClass="department-logo"
                  />
                  <div className="department-info">
                    {/* The full name, and only the name: the number and the
                        rest of the facts sit behind the (i), which shows on
                        hover. */}
                    <h2 className="department-name">
                      {selectedDepartment.official_name || selectedDepartment.common_name}
                    </h2>
                  </div>
                </>
              ) : (
                <div className="department-all-selected">
                  <Layers size={16} />
                  <span>All Agencies</span>
                </div>
              )}
              <ChevronDown
                size={16}
                className={`dropdown-chevron ${deptDropdownOpen ? 'rotated' : ''}`}
              />
            </button>

            {/* The facts the deleted second header carried, plus the ones it
                never did. A SIBLING of the dropdown button, never a child: a
                control inside a control is invalid markup, and the Hint
                primitive's own rule says the same. Every field here is already
                on the `selectedDepartment` row, so the (i) costs no fetch. */}
            {selectedDepartment && (
              <Hint
                className="department-info-hint"
                ariaLabel={`About ${selectedDepartment.official_name || selectedDepartment.common_name || 'this agency'}`}
                label={selectedDepartment.official_name || selectedDepartment.common_name}
                text={<DepartmentFacts dept={selectedDepartment} />}
              />
            )}

            {/* Dropdown Menu */}
            {deptDropdownOpen && (
              <div className="department-dropdown">
                <div className="dropdown-search">
                  <Search size={16} className="search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search agencies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="dropdown-search-input"
                  />
                </div>

                <div className="dropdown-list" role="listbox">
                  {/* State-wide used to be its own "All" button beside the
                      selector. It is a choice of agency scope, so it lives
                      with the other choices. */}
                  {!searchQuery.trim() && (
                    <div className={`dropdown-item-row ${!selectedDepartment ? 'selected' : ''}`} role="none">
                      <span className="dropdown-item-star dropdown-item-star--spacer" aria-hidden="true" />
                      <button
                        className="dropdown-item"
                        onClick={() => { onSelectAllDepartments?.(); setDeptDropdownOpen(false); setSearchQuery(''); }}
                        role="option"
                        aria-selected={!selectedDepartment}
                      >
                        <div className="dropdown-item-badge dropdown-item-badge--all"><Layers size={14} /></div>
                        <div className="dropdown-item-info">
                          <span className="dropdown-item-name">All agencies</span>
                          <span className="dropdown-item-code">California, state-wide</span>
                        </div>
                        {!selectedDepartment && <Check size={16} className="dropdown-item-check" />}
                      </button>
                    </div>
                  )}
                  {filteredDepartments.length > 0 ? (
                    filteredDepartments.map(dept => {
                      const isSelected = selectedDepartment?.department_id === dept.department_id;
                      const pinned = isPinned(dept.department_id);
                      const deptLabel = dept.acronym
                        ? (dept.official_name || dept.common_name)
                        : (dept.common_name || dept.official_name);
                      return (
                        /* Pin and select are SIBLINGS inside a non-interactive
                           row: a <button> nested in a <button> is invalid HTML,
                           and assistive tech either drops the inner control or
                           folds its text into the outer accessible name. */
                        <div
                          key={dept.department_id}
                          className={`dropdown-item-row ${isSelected ? 'selected' : ''}`}
                          role="none"
                        >
                          <button
                            className={`dropdown-item-star ${pinned ? 'pinned' : ''}`}
                            onClick={(e) => handleTogglePin(e, dept.department_id)}
                            title={pinned ? 'Remove from favorites' : 'Add to favorites'}
                            aria-label={`${pinned ? 'Unpin' : 'Pin'} ${deptLabel || 'agency'}`}
                          >
                            <Star size={14} fill={pinned ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            className="dropdown-item"
                            onClick={() => handleDepartmentSelect(dept)}
                            role="option"
                            aria-selected={isSelected}
                            aria-label={`Filter by ${deptLabel || 'agency'}`}
                          >
                            <div className="dropdown-item-badge">
                              {dept.acronym || dept.department_code?.slice(0, 3) || '?'}
                            </div>
                            <div className="dropdown-item-info">
                              <span className="dropdown-item-name">
                                {deptLabel}
                              </span>
                              {(dept.department_number || dept.bu_code) && (
                                <span className="dropdown-item-code">
                                  #{dept.department_number || dept.bu_code}
                                </span>
                              )}
                            </div>
                            {isSelected && (
                              <Check size={16} className="dropdown-item-check" />
                            )}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dropdown-empty">
                      No agencies found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick account picker: favourites or the selected list, one menu. */}
          {(accountLists.length > 0 || orderedPinnedDepts.length > 0) && (
            <div className="topnav-accounts" ref={accountsRef}>
              <button
                className={`topnav-accounts-btn ${accountsOpen ? 'open' : ''} ${selectedAccountList ? 'has-selection' : ''}`}
                onClick={() => setAccountsOpen((v) => !v)}
                aria-expanded={accountsOpen}
                aria-haspopup="listbox"
                title={selectedAccountList ? `My Accounts · ${selectedAccountList.name}` : 'My Accounts'}
              >
                <ListChecks size={16} className="topnav-accounts-icon" aria-hidden="true" />
                <span className="topnav-accounts-label">My Accounts</span>
                {quickRows.length > 0 && <span className="topnav-accounts-count">{quickRows.length}</span>}
                <ChevronDown size={13} className={`dropdown-chevron ${accountsOpen ? 'rotated' : ''}`} />
              </button>

              {accountsOpen && (
                <div className="topnav-accounts-menu">
                  {accountLists.length > 0 && (
                    <div className="topnav-accounts-lists" role="tablist" aria-label="Account lists">
                      <button
                        role="tab"
                        aria-selected={!selectedAccountList}
                        className={`topnav-accounts-list ${!selectedAccountList ? 'is-active' : ''}`}
                        onClick={() => onAccountListClear?.()}
                      >
                        <Star size={11} /> Favorites
                      </button>
                      {accountLists.map((list) => (
                        <button
                          key={list.id}
                          role="tab"
                          aria-selected={selectedAccountListId === list.id}
                          className={`topnav-accounts-list ${selectedAccountListId === list.id ? 'is-active' : ''}`}
                          onClick={() => onAccountListSelect?.(list.id)}
                          title={`${list.member_count} account${list.member_count === 1 ? '' : 's'}`}
                        >
                          <Users size={11} /> {list.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="topnav-accounts-rows" role="listbox" aria-label={quickLabel}>
                    {quickRows.length > 0 ? quickRows.map((row) => {
                      const isActive = selectedDepartment?.department_id === row.department_id;
                      return (
                        <button
                          key={row.key}
                          role="option"
                          aria-selected={isActive}
                          className={`topnav-accounts-row ${isActive ? 'is-active' : ''}`}
                          onClick={() => handleAccountMemberClick(row)}
                        >
                          <span className="dropdown-item-badge">{row.acronym || '?'}</span>
                          <span className="topnav-accounts-row-name">{row.name}</span>
                          {isActive && <Check size={14} className="dropdown-item-check" />}
                        </button>
                      );
                    }) : (
                      <div className="dropdown-empty">
                        {selectedAccountList
                          ? 'This list has no agencies yet.'
                          : 'Pin agencies with ★ in the agency menu, or pick a list above.'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
              </>
            )}
          </div>
        )}

        <div className="topnav-right">
          {/* Universal search — a magnifier until it is wanted; ⌘K still opens it. */}
          <div
            ref={searchWrapRef}
            className={`topnav-search ${searchOpen ? 'is-open' : ''}`}
            onFocusCapture={() => setSearchOpen(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setSearchOpen(false);
            }}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
          >
            <GlobalSearchBar onDepartmentSelect={onDepartmentSelect} />
          </div>
          {!searchOpen && (
            <button className="topnav-iconbtn" onClick={openSearch} aria-label="Search everything (⌘K)" title="Search everything  ⌘K">
              <Search size={18} />
            </button>
          )}

          {!filterHidden && !accountListMode && (
            <div className="topnav-time" ref={timeDropdownRef}>
              <button
                className={`topnav-time-btn ${timeDropdownOpen ? 'open' : ''}`}
                onClick={() => setTimeDropdownOpen(prev => !prev)}
                aria-expanded={timeDropdownOpen}
                aria-haspopup="dialog"
                title="Time range — applies to every time-aware view"
              >
                <CalendarRange size={14} />
                <span>{timeLabel}</span>
                <ChevronDown size={13} className={`dropdown-chevron ${timeDropdownOpen ? 'rotated' : ''}`} />
              </button>
              {timeDropdownOpen && (
                <div className="topnav-time-panel" role="dialog" aria-label="Time range">
                  <TimeRangePicker
                    value={timeRangeValue}
                    onChange={(rangeValue) => setTimePeriod(rangeValue)}
                    size="sm"
                    fiscalCalendar={fiscalCalendar}
                  />
                </div>
              )}
            </div>
          )}

          <NotificationBell
            notifications={notifications}
            onMarkRead={onMarkNotificationRead}
            onMarkAllRead={onMarkAllNotificationsRead}
            onClear={onClearNotifications}
          />
          <UserMenu
            currentUser={currentUser}
            onSettingsClick={onSettingsClick}
            onLogout={onLogout}
            onThemeToggle={onThemeToggle}
            theme={theme}
          />
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
